import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Centralized Axios instance with automatic token refresh.
 *
 * - Attaches the session_token as a Bearer header on every request.
 * - On 401 responses, attempts to refresh the token once via /auth/refresh.
 * - If refresh succeeds, retries the original request transparently.
 * - If refresh fails, clears session and redirects to login.
 */
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

// ─── Request Interceptor: attach session token ───────────────────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("session_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor: handle 401 + token refresh ─────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh on 401 and not already retried
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't try to refresh if the failing request IS the refresh call
    if (originalRequest.url?.includes("/auth/refresh")) {
      forceLogout();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue subsequent requests while refresh is in-flight
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const sessionToken = localStorage.getItem("session_token");
      const { data } = await axios.post(
        `${API_URL}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );

      // If backend returns a new session_token, update it; otherwise keep existing
      const updatedSessionToken = data.session_token || sessionToken;
      localStorage.setItem("session_token", updatedSessionToken);

      axiosInstance.defaults.headers.Authorization = `Bearer ${updatedSessionToken}`;
      originalRequest.headers.Authorization = `Bearer ${updatedSessionToken}`;

      processQueue(null, updatedSessionToken);
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      forceLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * Clears all session data and redirects to login.
 */
function forceLogout() {
  localStorage.clear();
  window.location.href = "/";
}

export default axiosInstance;
